import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Loader2, Printer, ShieldCheck, Search, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "../lib/api";
import { personMatch } from "../lib/searchText";

export const ConsentsLogDialog = ({ open, onOpenChange }) => {
  const [items, setItems] = useState(null);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState("");
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("signed");

  const load = () => {
    setLoading(true);
    api.signatureConsents()
      .then((r) => { setItems(r.items || []); setPending(r.pending || []); })
      .catch(() => toast.error("تعذّر تحميل سجل التعهّدات"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (open) load(); }, [open]);

  const requireResign = async (it) => {
    if (!window.confirm(`سيُطلب من «${it.employee_name || it.employee_id}» إعادة التوقيع عند الدخول التالي للتطبيق. متابعة؟`)) return;
    setResetting(it.employee_id);
    try {
      await api.resetSignatureConsent(it.employee_id);
      toast.success("تم — سيُطلب منه التوقيع مجدداً عند الدخول");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التنفيذ"); }
    finally { setResetting(""); }
  };

  const delConsent = async (it) => {
    if (!window.confirm(`حذف تعهّد «${it.employee_name || it.employee_id}» من السجل؟ سيُطلب منه التوقيع مجدداً.`)) return;
    try { await api.resetSignatureConsent(it.employee_id); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحذف"); }
  };

  const source = tab === "signed" ? (items || []) : pending;
  const filtered = source.filter((it) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return personMatch(it, s);
  });

  const printAll = () => {
    if (!filtered.length) { toast.warning("لا توجد تعهّدات للطباعة"); return; }
    const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const cards = filtered.map((it) => `
      <div class="card">
        <div class="hd"><b>${esc(it.employee_name || it.employee_id)}</b>
          <span class="muted">#${esc(it.employee_number)}${it.branch ? " · " + esc(it.branch) : ""}</span></div>
        <div class="muted">تاريخ القبول: ${esc(String(it.accepted_at || "").slice(0, 10))}</div>
        ${it.signature_id ? `<div class="sig"><div class="muted">التوقيع المعتمد:</div><img src="${fileUrl(it.signature_id)}"/></div>` : ""}
      </div>`).join("");
    const html = `<!doctype html><html dir="rtl" lang="ar"><head><style>@font-face{font-family:SaudiRiyal;src:url(/fonts/riyal.woff2) format("woff2");unicode-range:U+20C1}</style><meta charset="utf-8"><title>سجل تعهّدات التوقيع</title>
      <style>@page{size:A4;margin:12mm}body{font-family:SaudiRiyal,'IBM Plex Sans Arabic',Arial,sans-serif;color:#0f172a}
      h1{text-align:center;font-size:18px;margin:0 0 14px}
      .card{border:1px solid #cbd5e1;border-radius:10px;padding:10px 12px;margin-bottom:10px;page-break-inside:avoid}
      .hd{font-size:13px;margin-bottom:4px}.muted{color:#94a3b8;font-size:11px}
      .sig img{max-height:90px;border:1px solid #e2e8f0;border-radius:6px;background:#fff;margin-top:3px}</style></head>
      <body><h1>سجل تعهّدات التوقيع الإلكتروني</h1>${cards}
      <script>window.onload=function(){setTimeout(function(){window.print()},500)}</script></body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("فعّل النوافذ المنبثقة للطباعة"); return; }
    w.document.write(html); w.document.close();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto font-body" dir="rtl" data-testid="consents-log-dialog">
        <DialogHeader className="text-right">
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-blue-600" /> سجل تعهّدات التوقيع
          </DialogTitle>
          <DialogDescription className="text-right">تعهّدات المندوبين باعتماد توقيعهم الإلكتروني مع التواريخ والتواقيع.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-1.5 bg-slate-100 rounded-xl p-1">
          <button onClick={() => setTab("signed")} data-testid="consents-tab-signed"
            className={`flex-1 rounded-lg py-1.5 text-sm font-semibold transition ${tab === "signed" ? "bg-white shadow text-emerald-700" : "text-slate-500"}`}>
            وقّعوا ({(items || []).length})
          </button>
          <button onClick={() => setTab("pending")} data-testid="consents-tab-pending"
            className={`flex-1 rounded-lg py-1.5 text-sm font-semibold transition ${tab === "pending" ? "bg-white shadow text-amber-700" : "text-slate-500"}`}>
            لم يوقّعوا ({pending.length})
          </button>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="consents-search-input" placeholder="ابحث بالاسم أو الرقم..." value={q}
              onChange={(e) => setQ(e.target.value)} className="pr-9" />
          </div>
          <Button variant="outline" size="sm" onClick={printAll} disabled={!filtered.length} data-testid="consents-print-all-button" className="gap-1.5">
            <Printer className="h-4 w-4" /> طباعة
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : !filtered.length ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-slate-500" data-testid="consents-empty">
            {tab === "signed" ? "لا توجد تعهّدات مسجّلة بعد." : "جميع المندوبين وقّعوا التعهّد 🎉"}
          </div>
        ) : tab === "pending" ? (
          <div className="space-y-2">
            {filtered.map((it) => (
              <div key={it.employee_id} data-testid={`consent-pending-${it.employee_id}`}
                className="flex items-center justify-between rounded-xl border border-amber-100 bg-amber-50/50 px-4 py-2.5">
                <div className="min-w-0">
                  <div className="font-bold text-slate-900 truncate">{it.employee_name || it.employee_id}</div>
                  <div className="text-xs text-slate-400 font-mono-nums">#{it.employee_number}{it.branch ? ` · ${it.branch}` : ""}</div>
                </div>
                <span className="rounded-full bg-amber-100 text-amber-700 px-2.5 py-1 text-xs font-bold shrink-0">لم يوقّع بعد</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((it) => (
              <div key={it.employee_id} data-testid={`consent-row-${it.employee_id}`} className="rounded-2xl border border-slate-200 p-4 bg-white">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-bold text-slate-900 truncate">{it.employee_name || it.employee_id}</div>
                    <div className="text-xs text-slate-400 font-mono-nums">#{it.employee_number}{it.branch ? ` · ${it.branch}` : ""}</div>
                  </div>
                  <span className="rounded-full bg-emerald-100 text-emerald-700 px-2.5 py-1 text-xs font-bold font-mono-nums">
                    {String(it.accepted_at || "").slice(0, 10)}
                  </span>
                </div>
                {it.signature_id && (
                  <div className="mt-2.5">
                    <div className="text-xs font-semibold text-slate-500 mb-1">التوقيع المعتمد</div>
                    <img src={fileUrl(it.signature_id)} alt="signature" data-testid={`consent-signature-${it.employee_id}`}
                      className="max-h-28 rounded-lg border border-slate-200 bg-white" />
                  </div>
                )}
                <div className="mt-2.5 flex justify-end gap-2">
                  <Button variant="outline" size="sm" data-testid={`consent-delete-${it.employee_id}`} onClick={() => delConsent(it)} className="gap-1.5 border-red-200 text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /> حذف</Button>
                  <Button variant="outline" size="sm" data-testid={`consent-resign-${it.employee_id}`}
                    disabled={resetting === it.employee_id} onClick={() => requireResign(it)}
                    className="gap-1.5 border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700">
                    {resetting === it.employee_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                    طلب إعادة التوقيع
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
